import { useRouter } from 'next/router';
import { ExternalLinkWarningModal, isInternalUrl, normalizeUrl } from '@zg-rentals/satellite-components';
import { useUserWarnings } from '../../hooks/useUserWarnings';
import useQueryParamsNext from '../../hooks/useQueryParamsNext';

export const EXTERNAL_LINK_QUERY_PARAM = 'externalLink';

/**
 * When the inbox is opened from an email link carrying an `externalLink` query param,
 * auto-open the shared external-link warning modal for that URL. The query param is the source of
 * truth for the modal's open state — closing the modal clears it from the URL (history replace, so
 * the back button doesn't reopen it). Missing, invalid, or internal URLs render nothing.
 */
export default function InboxExternalLinkWarningModal(): JSX.Element | null {
  const router = useRouter();
  const { updateQueryParams } = useQueryParamsNext();
  const { externalLinkWarningData, isLoading } = useUserWarnings();

  const rawExternalLink = router.query[EXTERNAL_LINK_QUERY_PARAM];
  // Catch-all/duplicate params can arrive as an array; take the first value.
  const externalLink = Array.isArray(rawExternalLink) ? rawExternalLink[0] : rawExternalLink;
  const normalizedUrl = externalLink ? normalizeUrl(externalLink) : null;

  const handleClose = () => {
    updateQueryParams({
      queryParamValues: { [EXTERNAL_LINK_QUERY_PARAM]: null },
      shouldReplaceHistory: true,
    });
  };

  // Wait for the router to hydrate the query and for warnings to load before deciding — otherwise
  // a domain that's allow-listed only via the API (not the hardcoded fallback list) would briefly
  // flash the modal until `externalLinkWarningData` resolves.
  if (!router.isReady || isLoading) {
    return null;
  }

  // Only warn on a present, valid, genuinely external URL.
  if (!normalizedUrl || isInternalUrl(normalizedUrl, externalLinkWarningData?.allowListedDomains)) {
    return null;
  }

  return <ExternalLinkWarningModal url={normalizedUrl} warningData={externalLinkWarningData} onClose={handleClose} />;
}
